import { useState } from 'react'

import { type InitResult } from '@airwallex/components-sdk'
import { useModal } from '@procurify/ui'
import { useSelector } from 'react-redux'

import { makeSelectUser } from '@webapp/core/stores/global'
import { verifyIdentityUseCase } from '@webapp/platform/banking/business-logic/use-cases/VerifyIdentity'
import { useQueryAirwallexSdkInitialization } from '@webapp/platform/banking/hooks/useQueryAirwallexSdkInitialization'
import { useQueryScaAuthCode } from '../models/useQueryScaAuthCode'
import { ScaVerificationModal } from '../views/ScaVerificationModal'

interface IUseScaVerificationBaseProps {
  onError: (error: Error) => void
  phoneNumber?: string
  enabled?: boolean
  onInitialized?: (initResult: InitResult) => Promise<void>
  onVerified?: (token: string) => void
}

export const useScaVerificationBase = ({
  onError,
  phoneNumber = '',
  enabled = true,
  onInitialized = () => Promise.resolve(),
  onVerified = () => undefined,
}: IUseScaVerificationBaseProps) => {
  const modal = useModal()
  const user = useSelector(makeSelectUser())
  const email = user.email

  const [isVerifyIdentityInitialized, setIsVerifyIdentityInitialized] =
    useState(false)

  const { data: authData, isFetching: isLoadingAuth } = useQueryScaAuthCode({
    onError,
    enabled,
  })

  const { initResult, isInitializing } = useQueryAirwallexSdkInitialization({
    authData,
    onInitialized,
  })

  const initializeVerificationComponent = (scaSessionCode?: string): void => {
    if (!isVerifyIdentityInitialized) {
      verifyIdentityUseCase({
        mountElementId: 'sca-component-mount',
        email,
        phoneNumber,
        onError,
        onVerified: (token) => {
          onVerified(token)
          modal.hideModal()
          setIsVerifyIdentityInitialized(false)
        },
        onCancel: () => {
          modal.hideModal()
          setIsVerifyIdentityInitialized(false)
        },
        scaSessionCode,
      })
      setIsVerifyIdentityInitialized(true)
    }
  }

  const openScaVerificationModal = (scaSessionCode?: string) => {
    modal.showModal(ScaVerificationModal, {
      open: true,
      onClose: () => {
        modal.hideModal()
        setIsVerifyIdentityInitialized(false)
      },
    })

    initializeVerificationComponent(scaSessionCode)
  }

  return {
    isLoading: isLoadingAuth || isInitializing,
    isInitialized: !!initResult,
    initResult,

    openScaVerificationModal,
  }
}
